import { UniqueIdentifier } from "@dnd-kit/core";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import ForecastingIcon from "@redotech/redo-icons/forecasting.svg";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { ForecastingViewsContext } from "@redotech/redo-merchant-inventory-app/inventory-views";
import { ForecastingSystemView } from "@redotech/redo-model/views/default-forecasting-views";
import { SortableTree } from "@redotech/redo-web/arbiter-components/navigation/folder-tree/sortable-tree";
import { TreeItem } from "@redotech/redo-web/arbiter-components/navigation/folder-tree/types";
import { flattenTree } from "@redotech/redo-web/arbiter-components/navigation/folder-tree/utilities";
import { RedoSecondaryNavItem } from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-secondary-nav-item";
import { memo, useMemo } from "react";
import { useMatch } from "react-router-dom";
import {
  FolderWithView,
  treeToViewsAndFolders,
  viewsAndFoldersToTree,
} from "../orders/view-to-tree-utils";

export const ForecastingItem = memo(function ForecastingItem() {
  const routes = useRequiredContext(MerchantRoutesContext);
  const team = useRequiredContext(TeamContext);
  const {
    views,
    folders,
    openEditFolderModal,
    openCreateFolderModal,
    updateOrdering,
  } = useRequiredContext(ForecastingViewsContext);
  const urlPrefix = `${routes.inventoryManagementBaseUrl}/forecasting`;

  const isForecastingEnabled = team.settings.ims?.forecastingEnabled ?? false;

  const viewsWithoutFolders = useMemo(() => {
    return views?.filter((view) => !view.folderId) || [];
  }, [views]);

  const foldersWithViews = useMemo<FolderWithView<"forecasting">[]>(() => {
    return (
      folders?.map((folder) => {
        return {
          folder,
          views: views?.filter((view) => view.folderId === folder._id) || [],
        };
      }) || []
    );
  }, [folders, views]);

  const sortableItems = useMemo(() => {
    return viewsAndFoldersToTree({
      viewsWithoutFolders,
      foldersWithViews,
      urlPrefix,
      viewCounts: {},
      folderCounts: {},
      hideZeroNotifications: false,
    });
  }, [viewsWithoutFolders, foldersWithViews, urlPrefix]);

  const handleRequestEdit = useHandler((id: UniqueIdentifier) => {
    openEditFolderModal(id.toString());
  });

  const handleUpdateOrdering = useHandler((items: TreeItem[]) => {
    const flattenedItems = flattenTree(items);
    const result = treeToViewsAndFolders<"forecasting">(
      flattenedItems,
      views || [],
      folders || [],
    );
    updateOrdering(result.views, result.folders);
  });

  const anyChildSelected = useMatch(`${urlPrefix}/*`);

  if (!isForecastingEnabled) {
    return null;
  }

  return (
    <RedoSecondaryNavItem
      action={{
        type: "link",
        href: `${urlPrefix}/${ForecastingSystemView.ALL}`,
      }}
      anyChildSelected={!!anyChildSelected}
      FilledIcon={ForecastingIcon}
      friendlyName="Forecasting"
      id="forecasting"
      UnfilledIcon={ForecastingIcon}
    >
      <SortableTree
        items={sortableItems}
        onRequestEdit={handleRequestEdit}
        setItems={handleUpdateOrdering}
      />
      <RedoSecondaryNavItem
        action={{
          type: "button",
          onClick: () => {
            openCreateFolderModal();
          },
        }}
        friendlyName="+ Add new folder"
        id="forecasting-create-folder"
      />
    </RedoSecondaryNavItem>
  );
});
